import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { Patient } from '@chiroup/core/types/Patient.type';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { kioskSession } from './kioskSession';

// Kiosk service. Auth is a bearer-token session keyed off the device's
// per-install kiosk-id (stored in localStorage). The flow:
//   1) `pairInit` posts the kiosk-id; backend either issues a fresh
//      pair code (device unclaimed) or — once the clinic has claimed
//      it from the staff app — returns `paired: true` plus a session
//      token. We stash the token in `kioskSession`; main.tsx then
//      attaches it as `Authorization: Bearer <token>` on every
//      subsequent kiosk call.
//   2) `me` / `getRequest` / `createPendingPatient` / `forwardRequest`
//      are kiosk-scope authenticated; the wrapper resolves the kiosk
//      identity from the session row, no header juggling here.
//
// The legacy `x-kiosk-id` header has been retired. Existing kiosks
// that boot up with no stored session will hit a 401 on `me`,
// re-enter `pairInit`, get a token, and continue — no manual reset
// required.

const KIOSK_ID_KEY = 'kiosk-id';

export const getKioskId = (): string => {
  if (typeof window === 'undefined') return '';

  let id = window.localStorage.getItem(KIOSK_ID_KEY);

  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `kiosk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(KIOSK_ID_KEY, id);
  }

  return id;
};

export const clearKioskId = () => {
  if (typeof window === 'undefined') return;

  window.localStorage.removeItem(KIOSK_ID_KEY);
  kioskSession.clear();
};

export type KioskClinicInfo = {
  clinicId: number;
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  flags: string[];
  ehr?: boolean | number;
  country?: string;
  doNotAllowUnrelatedRegions?: boolean;
};

export type KioskPairInitResponse =
  | { paired: false; pairCode: string; alreadyPaired?: false }
  // Backend returns 409 when the kioskId already maps to a claimed
  // Kiosks row. The kiosk has lost its session token and the only
  // safe recovery path is for clinic staff to re-pair it through the
  // staff app. We surface this as a flagged response (rather than
  // throwing) so the bootstrap can render an explanatory UI.
  | { paired: false; alreadyPaired: true; pairCode?: never }
  | {
      paired: true;
      clinicId: number;
      location: number | null;
      token?: string;
      expiresAt?: string;
    };

export type KioskRequestBootstrap = {
  age: number | null;
  ccsFollowups?: string[];
  complaints?: any[];
  gender?: Genders;
  guardian?: boolean;
  guardianName?: string;
  language?: 'en' | 'es';
  methods?: any;
  patientName?: string;
  // Patient's preferred (or first) name — greeting on the kiosk welcome
  // screen. Empty string when neither is on file.
  preferredName?: string;
  // EHR clinics only (undefined otherwise): whether the patient has an
  // appointment today. Drives the welcome-screen branch between "no
  // appointment, see the front desk" and the check-in / forms path.
  appointmentFound?: boolean;
  prMap?: { [key: string]: string };
  steps: RequestStep[];
  validationCode: string;
  smsConsent?: string;
  smsConsentMarketing?: string;
};

// Explicit Authorization header for every authed kiosk call. We
// can't rely on `main.tsx`'s fallback chain (`portal → request →
// kiosk`) because a stale portal session in this same browser would
// take precedence and the kiosk would send a patient-scoped token to
// `/kiosk/*` — wrapper rejects it with "Session not permitted for
// this endpoint." Per-call `headers` override the global callback.
const kioskAuthHeaders = (): { Authorization: string } | Record<string, never> => {
  const token = kioskSession.getToken();

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const kioskService = () => {
  // Pair init is public — no token yet. We pass the kiosk-id in the
  // body; backend either issues a pair code or — if the row already
  // has clinicId set — returns 409 telling us to ask staff to re-pair
  // (we surface that as `alreadyPaired: true` so the UI can render an
  // explanatory message instead of a generic error).
  const pairInit = async (kioskId: string): Promise<KioskPairInitResponse> => {
    try {
      const res: KioskPairInitResponse = await ChiroUpAPI.post('api-noauth', '/kiosk/pair', {
        body: {
          kioskId,
          operatingSystem:
            typeof navigator !== 'undefined' ? navigator.platform || 'unknown' : 'unknown',
          browser: typeof navigator !== 'undefined' ? navigator.userAgent || 'unknown' : 'unknown',
        },
      });

      if (res?.paired && (res as { token?: string }).token) {
        const r = res as { token?: string; expiresAt?: string };
        kioskSession.setToken(r.token!, r.expiresAt);
      }

      return res;
    } catch (err) {
      // ChiroUpAPI normalizes Amplify rejections to
      // `{ response: { data: { statusCode, ...body } } }`. 409 means
      // the row exists but is already claimed — the kiosk can't pair
      // itself, staff has to re-pair via the staff app.
      const statusCode = (
        err as {
          response?: { data?: { statusCode?: number } };
        }
      )?.response?.data?.statusCode;
      if (statusCode === 409) {
        return { paired: false, alreadyPaired: true };
      }
      throw err;
    }
  };

  // Migration helper for kiosks paired under the legacy Cognito flow.
  // We scan localStorage for an Amplify-stashed Cognito access token
  // (`CognitoIdentityServiceProvider.<clientId>.<username>.accessToken`)
  // and POST it to /kiosk/migrate, which validates the JWT against
  // the kiosk user pool and hands back a PortalSession token. Returns
  // the new session response on success, or null when there's no
  // legacy token to migrate from. Same response shape as a successful
  // pair so the bootstrap can treat them identically.
  const migrateFromCognito = async (): Promise<{
    paired: true;
    clinicId: number;
    location: number | null;
    token: string;
    expiresAt: string;
  } | null> => {
    if (typeof window === 'undefined') return null;

    const ls = window.localStorage;
    let legacyToken: string | null = null;

    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (!key) continue;
      // Cognito token key shape — `CognitoIdentityServiceProvider.
      // <userPoolClientId>.<username>.accessToken`. This format is
      // identical in Amplify v5 and v6 (the legacy healthcom-app runs
      // v6 with the default localStorage token store), so this scan
      // matches regardless. accessToken holds a JWT signed by the kiosk
      // user pool; that's all the migrate endpoint needs.
      if (key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.accessToken')) {
        legacyToken = ls.getItem(key);
        if (legacyToken) break;
      }
    }

    if (!legacyToken) return null;

    try {
      const res = await ChiroUpAPI.post('api-noauth', '/kiosk/migrate', {
        body: {},
        headers: { Authorization: `Bearer ${legacyToken}` },
      });

      if (!res?.paired || !res.token) return null;

      kioskSession.setToken(res.token, res.expiresAt);

      // Migration succeeded — wipe every Cognito artifact we can find
      // so we never replay it and so the next boot doesn't waste a
      // round trip on /kiosk/migrate.
      const cognitoKeys: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const k = ls.key(i);
        if (k && k.startsWith('CognitoIdentityServiceProvider.')) cognitoKeys.push(k);
      }
      cognitoKeys.forEach(k => ls.removeItem(k));

      return res;
    } catch {
      return null;
    }
  };

  // Read-only poll endpoint. The kiosk hits this every few seconds
  // while waiting for the clinic to claim it. Three terminal-ish
  // outcomes plus the keep-polling default:
  //   - paired:true → claim redeemed, session token included.
  //   - alreadyPaired:true → row is claimed but the redemption window
  //     elapsed (or there's no in-flight claim). Kiosk has to be
  //     re-paired by staff.
  //   - codeStale:true → the kiosk's pair code is past the server's
  //     5-minute TTL or the row vanished entirely. Kiosk should
  //     re-init via /kiosk/pair to get a fresh code.
  //   - paired:false (no flags) → keep polling.
  const pairStatus = async (
    kioskId: string,
    pairCode: string
  ): Promise<
    | { paired: false; codeStale?: boolean; alreadyPaired?: boolean }
    | {
        paired: true;
        clinicId: number;
        location: number | null;
        token?: string;
        expiresAt?: string;
      }
  > => {
    const res = await ChiroUpAPI.get('api-noauth', '/kiosk/pair/status', {
      queryParams: { kioskId, pairCode },
    });

    if (res?.paired && res.token) {
      kioskSession.setToken(res.token, res.expiresAt);
    }

    return res;
  };

  const me = async (_kioskId: string): Promise<KioskClinicInfo> => {
    return ChiroUpAPI.get('api', '/kiosk/me', { headers: kioskAuthHeaders() });
  };

  const getRequest = async ({
    phone,
    patientId,
  }: {
    kioskId?: string;
    phone?: string;
    patientId?: string;
  }): Promise<KioskRequestBootstrap | { patients: Patient[] }> => {
    const queryParams: Record<string, string> = {};

    if (phone) queryParams.phone = phone;
    if (patientId) queryParams.patientId = patientId;

    return ChiroUpAPI.get('api', '/kiosk/requests', {
      queryParams,
      headers: kioskAuthHeaders(),
    });
  };

  const createPendingPatient = async (
    _kioskId: string,
    patient: Partial<Patient>
  ): Promise<Patient> => {
    return ChiroUpAPI.post('api', '/kiosk/pending-patient', {
      body: patient,
      headers: kioskAuthHeaders(),
    });
  };

  const forwardRequest = async (
    _kioskId: string,
    args: { requestId: string; method: string; maskedMethod: string }
  ): Promise<any> => {
    return ChiroUpAPI.post('api', '/kiosk/forward-request', {
      body: args,
      headers: kioskAuthHeaders(),
    });
  };

  return {
    pairInit,
    pairStatus,
    migrateFromCognito,
    me,
    getRequest,
    createPendingPatient,
    forwardRequest,
  };
};

export default kioskService();
